<template>
  <nav
    class="burger-menu"
    :class="{
      'burger-menu--open': isOpen,
    }"
    :aria-expanded="isOpen.toString()"
  >
    <button class="burger-menu__button" aria-label="menu" @click="toggleMenu">
      <span class="burger-menu__button_lines" />
    </button>
    <transition name="fade">
      <div
        v-if="isOpen"
        class="burger-menu__overlay"
        data-testid="menu-overlay"
        @click="handleClickOutsideMenu"
      >
        <div ref="menuRef" class="burger-menu__content">
          <section
            v-for="(section, index) in navigation"
            :key="`burger-menu-section-${index}`"
            class="burger-menu__section"
          >
            <h3 class="burger-menu__title">{{ $t(section.title) }}</h3>
            <ul>
              <router-link
                v-for="(item, itemIndex) in section.children"
                v-slot="{ href, navigate, isActive }"
                :key="`burger-menu-item-${itemIndex}`"
                :to="item.link || { name: item.routeName }"
                custom
              >
                <li :class="{ 'burger-menu__item': true, 'burger-menu__item--active': isActive }">
                  <a
                    :href="item.link ? item.link : href"
                    :target="item.link && '_blank'"
                    @click="navigateWrapper($event, navigate)"
                  >
                    <div class="burger-menu__icon-container">
                      <HdIcon :src="item.icon" :width="24" :height="24" />
                    </div>
                    <span>{{ $t(item.label) }}</span>
                  </a>
                </li>
              </router-link>
            </ul>
            <hr v-if="index < navigation.length - 1" class="burger-menu__section-separator" />
          </section>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import { HdIcon, ScrollingService } from 'homeday-blocks';
import { BURGER_MENU_NAVIGATION } from '@/config/BURGER_MENU_NAVIGATION';
import { GETTER } from '@/store/CONSTANTS';
import { USER_TYPE } from '@/config/CONSTANTS';

const { freezeScrolling, unfreezeScrolling } = ScrollingService;

export default {
  name: 'BurgerMenu',
  components: {
    HdIcon,
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    navigation() {
      if (this.userType === USER_TYPE.DEMAND) {
        const filteredMenu = BURGER_MENU_NAVIGATION.filter(
          (menu) => menu.title !== 'GENERAL.PRICE_ASSISTANT',
        );

        return filteredMenu;
      }

      return BURGER_MENU_NAVIGATION;
    },
    userType() {
      return this.$store.getters[GETTER.USER.USER_TYPE];
    },
  },
  beforeDestroy() {
    unfreezeScrolling(this.$el);
  },
  methods: {
    toggleMenu() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) freezeScrolling(this.$el);
      else unfreezeScrolling(this.$el);
    },
    navigateWrapper(event, navigate) {
      this.toggleMenu();
      return navigate(event);
    },
    handleClickOutsideMenu({ target: clickedElement }) {
      const menuElement = this.$refs.menuRef;

      if (menuElement && !menuElement.contains(clickedElement)) this.toggleMenu();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';

$transition-timing: 0.3s;

.burger-menu {
  &__button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    height: $sp-m;
    position: relative;
    width: 26px;

    &_lines,
    &_lines:before,
    &_lines:after {
      background-color: $primary-bg;
      border-radius: 4px;
      content: '';
      height: 2px;
      left: 0;
      position: absolute;
      transition: transform $transition-timing, background-color $transition-timing;
      width: 19px;
    }

    &_lines {
      bottom: 50%;

      &:before {
        top: 5px;
      }

      &:after {
        top: -5px;
      }
    }
  }

  &__content {
    background-color: $primary-bg;
    border-radius: $sp-s;
    height: 100%;
    padding: calc(#{$sp-m} + #{$sp-s}) $sp-m;
    position: relative;

    @include for('tablet') {
      height: auto;
    }
  }

  &__title {
    @include font('DS-200');
    color: getShade($quaternary-color, 80);
    font-weight: 400;
  }

  &__icon-container {
    background-color: $primary-bg;
    height: 24px;
    position: relative;
    min-width: 24px;

    svg {
      position: absolute;
    }
  }

  &__item > a {
    @include font('DS-100');
    font-weight: 400;
    margin-top: $sp-m;
    cursor: pointer;
    display: flex;
    align-items: center;
    color: getShade($primary-color, 110);
    text-decoration: none;

    span {
      margin-left: $sp-m;
    }
  }

  &__item--active > a {
    font-weight: 600;
  }

  &__section-separator {
    margin: $sp-m + $sp-s 0;
    height: 1px;
    border: none;
    background-color: getShade($quaternary-color, 50);
  }

  &__overlay {
    background-color: $primary-bg;
    left: 0;
    height: 100%;
    overflow-y: auto;
    padding-bottom: $header-height-mobile;
    position: fixed;
    top: $header-height-mobile;
    width: 100%;

    @include for('tablet') {
      background-color: transparent;
      bottom: 0;
      height: auto;
      left: unset;
      right: $sp-xs;
      top: calc(#{$header-height} + #{$sp-xs});
      padding: 0;
      width: 448px;
    }

    &::before {
      @include for('tablet') {
        background-color: rgba(0, 0, 0, 0.3);
        content: '';
        height: calc(100% - #{$header-height});
        left: 0;
        top: $header-height;
        position: fixed;
        width: 100%;
      }
    }
  }

  &--open {
    .burger-menu__button_lines {
      background-color: transparent;

      &:before,
      &:after {
        top: 0;
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }
}

.fade-enter-active {
  animation: fadeIn $transition-timing forwards;
}

.fade-leave-active {
  animation: fadeOut $transition-timing forwards;
}
</style>
